
import { Component, Input, OnInit, Output, EventEmitter, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as Cesium from 'cesium';
import { CoresService } from '../../../core/services/cores.service';
import { CreatorDialogComponent } from '../wrench-action-drawer/incident-creator-button/creator-dialog/creator-dialog.component';
import { SafeZoneMapEntity } from '../../../core/services/safe-zone-map-entity.service';
import { IncidentMapEntity } from '../../../core/services/incident-map-entity.service';
import { CameraService } from '../../../core/services/camera.service';
import { CoreService } from '../../../core/services/core.service';
import { DataContextService } from '../../../core/services/data-context.service';
import { TimelineService } from '../../../core/services/timeline.service';
import { WindowService } from '../../../core/services/window.service';

@Component({
  selector: 'app-safezone-creator',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './safezone-creator.component.html',
  styleUrls: ['./safezone-creator.component.css'],
})
export class SafezoneCreatorComponent implements OnInit {

  @Input() eventId!: string;
  @Output() done = new EventEmitter<{ success: boolean; points: any[] }>();
  @Output() cancel = new EventEmitter<void>();
  @Output() addSafeZoneStatus = new EventEmitter<boolean>();

  private handler?: Cesium.ScreenSpaceEventHandler;
  private viewer?: Cesium.Viewer;
  private safeZoneEntity?: SafeZoneMapEntity;
  points: any[] = [];

  constructor(
    @Optional() public activeModal: NgbActiveModal | null,
    private core: CoresService,
    private modalService: NgbModal,
    private coreService: CoreService,
    private cameraService: CameraService,
    private dataContextService: DataContextService,
    private windowService: WindowService,
    private timelineService: TimelineService
  ) {
  }

  ngOnInit(): void {
    this.viewer = this.core?.getViewer?.();
    if (!this.viewer) {
      return;
    }

    // Start listening for clicks right away (like AngularJS "setSafeZoneButtonClicked")
    this.setSafeZoneButtonClicked();
  }

  /**
   * Equivalent to AngularJS $scope.setSafeZoneButtonClicked
   * Adds click listener on the globe and creates the SafeZone
   */
  setSafeZoneButtonClicked(): void {
    this.removeHandler(); // ensure old handler removed

    this.handler = new Cesium.ScreenSpaceEventHandler(this.viewer!.scene.canvas);
    this.handler.setInputAction((click: any) => {
      const cartesian = this.viewer!.scene.pickPosition(click.position);
      if (!cartesian) return;

      const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
      const lon = Cesium.Math.toDegrees(cartographic.longitude);
      const lat = Cesium.Math.toDegrees(cartographic.latitude);

      // Geometry (like geo object in AngularJS)
      const geo = {
        type: 'Point',
        coordinates: [lon, lat]
      };

      // Create SafeZone Entity (matches AngularJS safeZoneEntity = new SafeZoneMapEntity(...))
      const safeZoneData = {
        incidentId: this.eventId,
        geometry: geo
      };

      this.safeZoneEntity = new SafeZoneMapEntity(safeZoneData, this.eventId, false, this.cameraService, this.core, this.coreService, this.windowService, this.dataContextService, this.modalService, this.timelineService);
      
      // Immediately add the entity to the map so the icon appears on the globe
      if (this.safeZoneEntity && this.viewer) {
        this.dataContextService.addMapEntity(this.safeZoneEntity);
      }
      
      // Proceed to modal (like $scope.finishedSafeZoneClicked)
      this.finishedSafeZoneClicked(cartesian);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  /**
   * Equivalent to $scope.finishedSafeZoneClicked
   * Opens the Creator dialog modal, handles results
   */
  private finishedSafeZoneClicked(cartesian: any): void {
    const modalRef = this.modalService.open(CreatorDialogComponent, { size: 'lg' });
    modalRef.componentInstance.dialogData = {
      entitytype: 'Safezone',
      point: cartesian,
      title: 'Create Safezone',
      data: this.safeZoneEntity,
      SafeZoneMapEntity: SafeZoneMapEntity,
      IncidentMapEntity: IncidentMapEntity,
    };

    modalRef.result
      .then((result) => {
        // If user cancelled, remove the entity and restart click mode
        if (result === 'cancel') {
          // Remove the entity from the map (remove the icon from the globe)
          if (this.safeZoneEntity && this.viewer) {
            if (this.safeZoneEntity.addedToMap) {
              this.dataContextService.removeMapEntity(this.safeZoneEntity, true);
            }
            const entityId = this.safeZoneEntity.getId();
            if (entityId) {
              const entity = this.viewer.entities.getById(entityId);
              if (entity) {
                this.viewer.entities.remove(entity);
              }
            }
            this.safeZoneEntity = undefined;
          }
          this.setSafeZoneButtonClicked();
          return;
        }

        // Just remove the handler
        this.removeHandler();
        try {
          // notify parent to close overlay and open incident visualizer
          this.addSafeZoneStatus.emit(false);
          this.done.emit({ success: true, points: this.points });
        } catch (e) {}
      })
      .catch((error) => {
        console.error('Modal closed with error:', error);
        // Remove the entity from the map when modal is dismissed
        if (this.safeZoneEntity && this.viewer) {
          if (this.safeZoneEntity.addedToMap) {
            this.dataContextService.removeMapEntity(this.safeZoneEntity, true);
          }
          const entityId = this.safeZoneEntity.getId();
          if (entityId) {
            const entity = this.viewer.entities.getById(entityId);
            if (entity) {
              this.viewer.entities.remove(entity);
            }
          }
          this.safeZoneEntity = undefined;
        }
        this.cancelSafeZoneClicked();
      })
      .finally(() => {
      });
  }

  /**
   * Equivalent to $scope.cancelSafeZoneClicked
   * Removes entity, handler, and closes active modal if any
   */
  cancelSafeZoneClicked(): void {
    this.cleanup();

    if (this.activeModal) {
      this.activeModal.dismiss();
    } else {
      this.cancel.emit();
    }

    // Notify parent if needed
    this.addSafeZoneStatus.emit(false);
  }

  /**
   * Equivalent to cleanup() in AngularJS
   * Removes Cesium entity and event handler
   */
  private cleanup(): void {
    if (this.safeZoneEntity && this.viewer) {
      // Remove the entity from the map (remove the icon from the globe)
      if (this.safeZoneEntity.addedToMap) {
        this.dataContextService.removeMapEntity(this.safeZoneEntity, true);
      }
      // Also try removing directly from viewer by ID
      const entityId = this.safeZoneEntity.getId();
      if (entityId) {
        const entity = this.viewer.entities.getById(entityId);
        if (entity) {
          this.viewer.entities.remove(entity);
        }
      }
      this.safeZoneEntity = undefined;
    }
    this.removeHandler();
  }

  private removeHandler(): void {
    if (this.handler) {
      this.handler.destroy();
      this.handler = undefined;
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}